import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, Car, Users, Briefcase, Calendar as CalendarIcon, Clock, ArrowRight, CreditCard, Money, MapPin, DownloadSimple, Copy, CheckCircle, NavigationArrow, AirplaneTakeoff, Train, CastleTurret, MapTrifold, Timer, FloppyDisk } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { fr, es, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dateLocales = { fr, es, en: enUS };

// ─── Address Autocomplete Component ───
const AddressAutocomplete = ({ value, onChange, onPlaceSelect, placeholder, required, testId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  const fetchSuggestions = async (input) => {
    if (input.length < 3) { setSuggestions([]); setShowDropdown(false); return; }
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/places/autocomplete`, { params: { input } });
      const items = resp.data.suggestions || [];
      setSuggestions(items);
      setShowDropdown(items.length > 0);
    } catch (e) {
      console.error("Autocomplete error:", e);
      setSuggestions([]);
    } finally { setLoading(false); }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = async (suggestion) => {
    setShowDropdown(false);
    const pred = suggestion.placePrediction;
    const placeId = pred.placeId;
    const displayText = pred.text?.text || pred.structuredFormat?.mainText?.text || "";
    onChange(displayText);
    try {
      const resp = await axios.get(`${API}/places/details/${placeId}`);
      const data = resp.data;
      onPlaceSelect({
        place_id: placeId,
        name: data.displayName?.text || displayText,
        formatted_address: data.formattedAddress || displayText,
        types: data.types || [],
        location: data.location || null,
      });
      onChange(data.formattedAddress || displayText);
    } catch (e) {
      console.error("Place details error:", e);
      onPlaceSelect({ place_id: placeId, name: displayText, formatted_address: displayText, types: [], location: null });
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <input
        type="text"
        value={value}
        onChange={handleInput}
        onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
        required={required}
        className="input-luxury w-full bg-transparent"
        placeholder={placeholder}
        data-testid={testId}
        autoComplete="off"
      />
      {showDropdown && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-[#1A1A1A]/15 shadow-lg max-h-60 overflow-y-auto" data-testid={`${testId}-dropdown`}>
          {suggestions.map((s) => {
            const pred = s.placePrediction;
            const mainText = pred?.structuredFormat?.mainText?.text || pred?.text?.text || "";
            const secondaryText = pred?.structuredFormat?.secondaryText?.text || "";
            return (
              <button
                key={pred?.placeId || mainText}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(s)}
                className="w-full text-left px-4 py-3 hover:bg-[#EDEBE4] transition-colors border-b border-[#1A1A1A]/5 last:border-b-0"
              >
                <p className="text-[#1A1A1A] text-sm font-medium">{mainText}</p>
                {secondaryText && <p className="text-[#1A1A1A]/50 text-xs">{secondaryText}</p>}
              </button>
            );
          })}
          {loading && <div className="px-4 py-3 text-[#1A1A1A]/40 text-xs">Recherche...</div>}
        </div>
      )}
    </div>
  );
};

// ─── Auto-detection logic ───
const detectPlace = (place) => {
  if (!place) return null;
  const types = place.types || [];
  const name = (place.name || "").toLowerCase();
  const addr = (place.formatted_address || "").toLowerCase();
  const combined = name + " " + addr;

  if (types.includes("airport") || combined.includes("charles de gaulle") || combined.includes("cdg") || combined.includes("orly") || combined.includes("le bourget") || combined.includes("aéroport") || combined.includes("aeroport"))
    return "airport";
  if (types.includes("train_station") || types.includes("transit_station") || combined.includes("gare du nord") || combined.includes("gare de lyon") || combined.includes("gare de l'est") || combined.includes("gare montparnasse") || combined.includes("gare saint-lazare") || combined.includes("gare d'austerlitz") || combined.includes("gare de bercy"))
    return "station";
  if (combined.includes("disney"))
    return "disneyland";
  if (combined.includes("château de versailles") || combined.includes("chateau de versailles") || (combined.includes("versailles") && (combined.includes("château") || combined.includes("chateau") || combined.includes("palace"))))
    return "versailles";
  return null;
};

// Detect if a place is in Paris (not a special location like airport/station)
const isParis = (place) => {
  if (!place) return false;
  // If this place is itself a special location, don't count it as "Paris"
  if (detectPlace(place)) return false;
  const addr = (place.formatted_address || "").toLowerCase();
  // Check for "Paris" as a word or 75xxx postal codes
  return /\bparis\b/.test(addr) || /\b75\d{3}\b/.test(addr);
};

const resolvePackage = (pickupPlace, dropoffPlace) => {
  const pickupType = detectPlace(pickupPlace);
  const dropoffType = detectPlace(dropoffPlace);
  const pickupIsParis = isParis(pickupPlace);
  const dropoffIsParis = isParis(dropoffPlace);

  // Track special location info for UI (flight/train number field)
  const specialInfo = {
    hasAirport: pickupType === "airport" || dropoffType === "airport",
    hasStation: pickupType === "station" || dropoffType === "station",
    withinParis: pickupIsParis && dropoffIsParis,
  };

  // Forfaits only apply when Paris is at one end
  // Check pickup is special → other end (dropoff) must be in Paris
  if (pickupType && dropoffIsParis) {
    if (pickupType === "airport") return { id: "airport_arrivee", type: "airport_arrival", ...specialInfo };
    if (pickupType === "station") return { id: "ville_gares_arrivee", type: "station_arrival", ...specialInfo };
    if (pickupType === "disneyland") return { id: "disneyland", type: "disneyland", ...specialInfo };
    if (pickupType === "versailles") return { id: "versailles", type: "versailles", ...specialInfo };
  }

  // Check dropoff is special → other end (pickup) must be in Paris
  if (dropoffType && pickupIsParis) {
    if (dropoffType === "airport") return { id: "airport", type: "airport", ...specialInfo };
    if (dropoffType === "station") return { id: "ville_gares", type: "station", ...specialInfo };
    if (dropoffType === "disneyland") return { id: "disneyland", type: "disneyland", ...specialInfo };
    if (dropoffType === "versailles") return { id: "versailles", type: "versailles", ...specialInfo };
  }

  // No forfait: either no special location, or other end is not Paris → km rate
  return { id: "custom_km", type: "km", ...specialInfo };
};

const DETECTION_ICONS = {
  airport: AirplaneTakeoff, airport_arrival: AirplaneTakeoff,
  station: Train, station_arrival: Train,
  disneyland: CastleTurret, versailles: CastleTurret,
  km: NavigationArrow,
};
const DETECTION_KEYS = {
  airport: "detected_airport", airport_arrival: "detected_airport_arrival",
  station: "detected_station", station_arrival: "detected_station_arrival",
  disneyland: "detected_disneyland", versailles: "detected_versailles",
  km: "detected_km",
};

// ─── Main Page ───
const DeplacementsPage = () => {
  const { t, i18n } = useTranslation();
  const [vehiclePrices, setVehiclePrices] = useState({});
  const [kmRates, setKmRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState();
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingRef, setBookingRef] = useState("");
  const [whatsappText, setWhatsappText] = useState("");
  const [copied, setCopied] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(null);
  const [missionType, setMissionType] = useState("simple");
  const [hourlyDuration, setHourlyDuration] = useState("hourly_3h");
  const [detectedPkg, setDetectedPkg] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [calculatingDistance, setCalculatingDistance] = useState(false);
  const [pickupPlace, setPickupPlace] = useState(null);
  const [dropoffPlace, setDropoffPlace] = useState(null);
  const [formData, setFormData] = useState({
    client_name: "", client_email: "", client_phone: "",
    vehicle_type: "classe_e",
    pickup_address: "", dropoff_address: "",
    flight_train_number: "",
    pickup_date: "", pickup_time: "09:00",
    passengers: 1, luggage: 0,
    special_requests: "", payment_method: "online",
  });

  const dateLoc = dateLocales[i18n.language?.split("-")[0]] || fr;

  useEffect(() => { fetchPackages(); }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setVehiclePrices(response.data.vehicle_prices || {});
      setKmRates(response.data.km_rates || {});
    } catch (error) { console.error("Error fetching packages:", error); }
  };

  // Recalculate detection when either place changes
  const updateDetection = useCallback(async (pickup, dropoff) => {
    if (!pickup || !dropoff) { setDetectedPkg(null); setDistanceKm(null); return; }
    const pkg = resolvePackage(pickup, dropoff);
    setDetectedPkg(pkg);
    if (pkg.id === "custom_km" && pickup.location && dropoff.location) {
      setCalculatingDistance(true);
      try {
        const resp = await axios.get(`${API}/places/distance`, {
          params: {
            origin_lat: pickup.location.latitude,
            origin_lng: pickup.location.longitude,
            dest_lat: dropoff.location.latitude,
            dest_lng: dropoff.location.longitude,
            origin_place_id: pickup.place_id,
            dest_place_id: dropoff.place_id,
          }
        });
        setDistanceKm(resp.data.distance_km);
      } catch (e) { console.error("Distance error:", e); setDistanceKm(null); }
      finally { setCalculatingDistance(false); }
    } else {
      setDistanceKm(null);
    }
  }, []);

  const enrichAddressWithPlaceName = (place) => {
    if (!place) return place.formatted_address || "";
    const name = place.name || "";
    const addr = place.formatted_address || "";
    // If no name or name already in address, return as-is
    if (!name || addr.toLowerCase().includes(name.toLowerCase())) return addr;
    // Always prefix the place name (hotel, restaurant, airport, station, etc.)
    return `${name} — ${addr}`;
  };

  const onPickupPlaceSelect = (place) => {
    setPickupPlace(place);
    const enriched = enrichAddressWithPlaceName(place);
    setFormData(p => ({ ...p, pickup_address: enriched }));
    if (missionType === "simple") updateDetection(place, dropoffPlace);
  };

  const onDropoffPlaceSelect = (place) => {
    setDropoffPlace(place);
    const enriched = enrichAddressWithPlaceName(place);
    setFormData(p => ({ ...p, dropoff_address: enriched }));
    if (missionType === "simple") updateDetection(pickupPlace, place);
  };

  const calculateTotal = () => {
    if (missionType === "hourly") {
      return vehiclePrices[formData.vehicle_type]?.[hourlyDuration] || 0;
    }
    if (!detectedPkg) return 0;
    if (detectedPkg.id === "custom_km") {
      const km = kmRates[formData.vehicle_type];
      if (!km) return 0;
      const minimum = detectedPkg.withinParis ? km.minimum_paris : km.minimum_outside;
      if (!distanceKm) return minimum;
      return Math.max(distanceKm * km.rate, minimum);
    }
    return vehiclePrices[formData.vehicle_type]?.[detectedPkg.id] || 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const packageId = missionType === "hourly" ? hourlyDuration : (detectedPkg?.id || "custom_km");
      const bookingData = {
        ...formData,
        package_id: packageId,
        pickup_date: date ? format(date, "yyyy-MM-dd") : "",
        ...(packageId === "custom_km" && distanceKm ? { distance_km: distanceKm } : {}),
        ...(packageId === "custom_km" ? { within_paris: detectedPkg?.withinParis || false } : {}),
      };
      const bookingRes = await axios.post(`${API}/booking`, bookingData);
      if (bookingRes.data.success) {
        if (formData.payment_method === "online") {
          const checkoutRes = await axios.post(`${API}/checkout/session`, {
            booking_id: bookingRes.data.booking_id,
            origin_url: window.location.origin,
          });
          if (checkoutRes.data.url) window.location.href = checkoutRes.data.url;
        } else {
          setBookingRef(bookingRes.data.booking_id);
          setWhatsappText(bookingRes.data.whatsapp_text || "");
          setBookingSuccess(true);
          toast.success(t("deplacements.success_title"));
        }
      }
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(error.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const bookableVehicles = [
    { id: "classe_e", name: "Mercedes Classe E", capacity: 3, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/stw5s5ky_703D55C7-6491-4A1D-BBD1-3724BB576F38.png" },
    { id: "classe_s", name: "Mercedes Classe S", capacity: 3, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/8up45an8_8CF1CAA1-D304-4DC3-99AE-C4B9AE562F84.jpeg" },
    { id: "classe_v", name: "Mercedes Classe V", capacity: 7, image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/bat48e3f_E8F9F042-84D5-4B63-87E0-68156E0CD47E.png" },
  ];
  const quoteOnlyVehicles = [
    { id: "range_rover", name: "Range Rover", link: "/vehicules/range-rover" },
    { id: "classe_g", name: "Mercedes Classe G", link: "/vehicules/classe-g" },
    { id: "maybach", name: "Mercedes-Maybach Classe S", link: "/vehicules/maybach" },
  ];
  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    return slots;
  };
  const timeSlots = generateTimeSlots();
  const isArrivalDetected = detectedPkg && (detectedPkg.type === "airport_arrival" || detectedPkg.type === "station_arrival");
  const isAirportOrStation = detectedPkg && (detectedPkg.hasAirport || detectedPkg.hasStation);
  const total = calculateTotal();
  const getDetectedLabel = () => detectedPkg ? t(`deplacements.${DETECTION_KEYS[detectedPkg.type]}`) : null;
  const DetectedIcon = detectedPkg ? DETECTION_ICONS[detectedPkg.type] : null;

  const handleCopyWhatsapp = () => { navigator.clipboard.writeText(whatsappText).then(() => { setCopied(true); toast.success("Texte copié !"); setTimeout(() => setCopied(false), 3000); }); };
  const handleDownloadPdf = () => { window.open(`${API}/booking/${bookingRef}/pdf`, "_blank"); };

  const handleSaveDraft = async () => {
    if (!formData.client_email) { toast.error("Veuillez renseigner votre email pour enregistrer le brouillon"); return; }
    setSavingDraft(true);
    try {
      const packageId = missionType === "hourly" ? hourlyDuration : (detectedPkg?.id || "");
      const draftData = {
        ...formData,
        package_id: packageId,
        pickup_date: date ? format(date, "yyyy-MM-dd") : "",
        ...(packageId === "custom_km" && distanceKm ? { distance_km: distanceKm } : {}),
        ...(packageId === "custom_km" ? { within_paris: detectedPkg?.withinParis || false } : {}),
      };
      const { data } = await axios.post(`${API}/booking/draft`, draftData);
      if (data.success) {
        setDraftSaved(data);
        toast.success("Brouillon enregistré — un email vous a été envoyé !");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally { setSavingDraft(false); }
  };

  // ─── Success Screen ───
  if (bookingSuccess) {
    const vehicleName = bookableVehicles.find(v => v.id === formData.vehicle_type)?.name || formData.vehicle_type;
    const refShort = bookingRef.slice(0, 8).toUpperCase();
    const missionLabel = missionType === "hourly" ? t("deplacements.mission_hourly") : t("deplacements.mission_simple");
    return (
      <main className="pt-20 bg-[#EDEBE4] min-h-screen" data-testid="booking-success">
        <div className="max-w-3xl mx-auto px-6 py-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check size={32} weight="bold" className="text-green-600" /></div>
              <h1 className="font-heading text-3xl text-[#1A1A1A] mb-2">{t("deplacements.success_title")}</h1>
              <p className="text-[#1A1A1A]/70 font-light">{t("deplacements.success_msg")}</p>
            </div>
            <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-10 mb-6">
              <div className="flex justify-between items-start mb-6">
                <div><h2 className="font-heading text-xl tracking-[0.2em] text-[#1A1A1A] uppercase">Altura</h2><p className="text-[#1A1A1A]/60 text-xs mt-1">Bon de commande</p></div>
                <div className="text-right"><p className="text-[#1A1A1A] font-mono text-sm">ALT-{refShort}</p><p className="text-[#1A1A1A]/60 text-xs">{date ? format(date, "dd/MM/yyyy") : ""}</p></div>
              </div>
              <div className="border-t border-[#1A1A1A]/10 pt-6 space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div><p className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide mb-1">{t("deplacements.name_label")}</p><p className="text-[#1A1A1A]">{formData.client_name}</p></div>
                  <div><p className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide mb-1">{t("deplacements.phone_label")}</p><p className="text-[#1A1A1A]">{formData.client_phone}</p></div>
                </div>
                <div className="border-t border-[#1A1A1A]/10 pt-4 space-y-3">
                  <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.mission_type")}</span><span className="text-[#1A1A1A]">{missionLabel}</span></div>
                  <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.vehicle_label")}</span><span className="text-[#1A1A1A] font-medium">{vehicleName}</span></div>
                  {detectedPkg && <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.service_label")}</span><span className="text-[#1A1A1A]">{getDetectedLabel()}</span></div>}
                  {distanceKm && <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.distance_label")}</span><span className="text-[#1A1A1A]">{distanceKm} km</span></div>}
                  <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.date_label")}</span><span className="text-[#1A1A1A]">{date ? format(date, "dd/MM/yyyy", { locale: dateLoc }) : ""} - {formData.pickup_time}</span></div>
                  <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.pickup_address")}</span><span className="text-[#1A1A1A] text-right max-w-[60%]">{formData.pickup_address}</span></div>
                  {formData.dropoff_address && <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{t("deplacements.dropoff_address")}</span><span className="text-[#1A1A1A] text-right max-w-[60%]">{formData.dropoff_address}</span></div>}
                  {formData.flight_train_number && <div className="flex justify-between"><span className="text-[#1A1A1A]/60">{detectedPkg?.hasAirport ? t("deplacements.flight_number") : t("deplacements.train_number")}</span><span className="text-[#1A1A1A]">{formData.flight_train_number}</span></div>}
                </div>
                <div className="border-t border-[#1A1A1A]/10 pt-4 flex justify-between items-center">
                  <span className="text-[#1A1A1A]/70 font-medium">{t("deplacements.total")}</span>
                  <span className="font-heading text-2xl text-[#1A1A1A]">{total.toFixed(2)}€</span>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              <button onClick={handleDownloadPdf} className="btn-dark flex items-center justify-center gap-3 w-full" data-testid="download-pdf-btn"><DownloadSimple size={18} weight="bold" />Télécharger le PDF</button>
              <button onClick={handleCopyWhatsapp} className="btn-primary flex items-center justify-center gap-3 w-full" data-testid="copy-whatsapp-btn">{copied ? <CheckCircle size={18} weight="bold" /> : <Copy size={18} weight="bold" />}{copied ? "Copié !" : "Copier pour WhatsApp"}</button>
            </div>
            <div className="text-center">
              <button onClick={() => { setBookingSuccess(false); setBookingRef(""); setWhatsappText(""); setCopied(false); setDetectedPkg(null); setDistanceKm(null); setPickupPlace(null); setDropoffPlace(null); setFormData({...formData, client_name:"", client_email:"", client_phone:"", pickup_address:"", dropoff_address:"", flight_train_number:"", special_requests:""}); setDate(undefined); }}
                className="text-[#1A1A1A]/60 text-sm hover:text-[#1A1A1A] transition-colors inline-flex items-center gap-2" data-testid="new-booking-btn">{t("deplacements.success_new")}<ArrowRight size={14} weight="bold" /></button>
            </div>
          </motion.div>
        </div>
      </main>
    );
  }

  // ─── Main Page ───
  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="deplacements-page">
      <SEO title="Réserver un Chauffeur Privé" path="/services/deplacements" description="Réservez votre chauffeur privé à Paris avec ALTURA. Transferts aéroport CDG et Orly, gares, Disneyland, Versailles. Mercedes Classe E, S, V. Tarifs à partir de 70€." />
      <section className="relative h-[50vh] min-h-[400px] flex items-center">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_parisgo/artifacts/mukuhgam_9D4041BE-84BD-4DB0-B0A6-3A210E81AFAF.png" alt="Intérieur luxueux Mercedes - ALTURA" className="w-full h-full object-cover" />
          <div className="hero-overlay-side absolute inset-0" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 w-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-md hero-text-backdrop">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-4">{t("deplacements.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none">{t("deplacements.hero_title_1")}<br /><span className="italic">{t("deplacements.hero_title_2")}</span></h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#EDEBE4]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Info panel */}
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-8">{t("deplacements.info_title")}</h2>
              <ul className="space-y-6 mb-12">
                {[t("deplacements.info_1"), t("deplacements.info_2"), t("deplacements.info_3"), t("deplacements.info_4")].map((item) => (
                  <li key={item} className="flex items-start gap-4"><Check size={20} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" /><span className="text-[#1A1A1A]/80 font-light">{item}</span></li>
                ))}
              </ul>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-6">{t("deplacements.vehicle_label")}</h3>
              <div className="space-y-3 mb-10">
                {bookableVehicles.map((v) => (
                  <div key={v.id} className={`bg-white border px-5 py-3 transition-all cursor-pointer ${formData.vehicle_type === v.id ? "border-[#1A1A1A]" : "border-[#1A1A1A]/10"}`} onClick={() => setFormData((p) => ({ ...p, vehicle_type: v.id }))}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3"><Car size={20} weight="thin" className="text-[#1A1A1A]" /><span className="text-[#1A1A1A] text-sm">{v.name}</span><span className="text-[#1A1A1A]/40 text-xs">{v.capacity} pass.</span></div>
                      <span className="text-[#1A1A1A]/60 text-xs">{t("deplacements.from_price")} {kmRates[v.id]?.minimum || 70}€</span>
                    </div>
                    {formData.vehicle_type === v.id && (
                      <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-3 overflow-hidden">
                        <img src={v.image} alt={v.name} className="w-full h-32 object-cover rounded-sm" />
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
              <h3 className="font-heading text-xl text-[#1A1A1A] mb-6">{t("deplacements.quote_only")}</h3>
              <div className="space-y-3">
                {quoteOnlyVehicles.map((v) => (
                  <Link key={v.id} to={v.link} className="flex items-center justify-between bg-white border border-[#1A1A1A]/10 px-5 py-3 group hover:border-[#1A1A1A]/30 transition-colors" data-testid={`quote-vehicle-${v.id}`}>
                    <div className="flex items-center gap-3"><Car size={20} weight="thin" className="text-[#1A1A1A]" /><span className="text-[#1A1A1A] text-sm group-hover:text-[#1A1A1A]/70">{v.name}</span></div>
                    <span className="text-[#1A1A1A]/50 text-xs group-hover:text-[#1A1A1A]/70 flex items-center gap-1">Devis <ArrowRight size={12} /></span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* Booking Form */}
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="bg-white border border-[#1A1A1A]/10 p-8 lg:p-12">
                <h2 className="font-heading text-2xl text-[#1A1A1A] mb-8">{t("deplacements.title")}</h2>
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">

                  {/* Mission Type */}
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-3">{t("deplacements.mission_type")} *</label>
                    <div className="grid grid-cols-2 gap-0" data-testid="mission-type-toggle">
                      <button type="button" onClick={() => { setMissionType("simple"); setDetectedPkg(null); setDistanceKm(null); }}
                        className={`p-4 border text-left transition-all ${missionType === "simple" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 hover:border-[#1A1A1A]/40 text-[#1A1A1A]"}`} data-testid="mission-simple-btn">
                        <div className="flex items-center gap-3 mb-1"><MapTrifold size={20} weight={missionType === "simple" ? "bold" : "light"} /><span className="text-sm font-medium">{t("deplacements.mission_simple")}</span></div>
                        <p className={`text-xs ${missionType === "simple" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>{t("deplacements.mission_simple_desc")}</p>
                      </button>
                      <button type="button" onClick={() => { setMissionType("hourly"); setDetectedPkg(null); setDistanceKm(null); }}
                        className={`p-4 border border-l-0 text-left transition-all ${missionType === "hourly" ? "border-[#1A1A1A] bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/20 hover:border-[#1A1A1A]/40 text-[#1A1A1A]"}`} data-testid="mission-hourly-btn">
                        <div className="flex items-center gap-3 mb-1"><Timer size={20} weight={missionType === "hourly" ? "bold" : "light"} /><span className="text-sm font-medium">{t("deplacements.mission_hourly")}</span></div>
                        <p className={`text-xs ${missionType === "hourly" ? "text-white/70" : "text-[#1A1A1A]/50"}`}>{t("deplacements.mission_hourly_desc")}</p>
                      </button>
                    </div>
                  </div>

                  {/* Client info */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.name_label")} *</label>
                      <input type="text" name="client_name" value={formData.client_name} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("deplacements.name_placeholder")} data-testid="input-client-name" /></div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.email_label")} *</label>
                      <input type="email" name="client_email" value={formData.client_email} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("deplacements.email_placeholder")} data-testid="input-client-email" /></div>
                  </div>
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.phone_label")} *</label>
                    <input type="tel" name="client_phone" value={formData.client_phone} onChange={handleChange} required className="input-luxury w-full bg-transparent" placeholder={t("deplacements.phone_placeholder")} data-testid="input-client-phone" /></div>

                  {/* Vehicle */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.vehicle_label")} *</label>
                    <Select value={formData.vehicle_type} onValueChange={(v) => setFormData((p) => ({ ...p, vehicle_type: v }))}>
                      <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-vehicle"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-[#1A1A1A]/10">
                        {bookableVehicles.map((v) => <SelectItem key={v.id} value={v.id} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{v.name} — {v.capacity} pass. max</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {formData.vehicle_type && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="mt-3 overflow-hidden rounded-sm border border-[#1A1A1A]/10">
                        <img src={bookableVehicles.find(v => v.id === formData.vehicle_type)?.image} alt={bookableVehicles.find(v => v.id === formData.vehicle_type)?.name} className="w-full h-40 object-cover" />
                      </motion.div>
                    )}
                  </div>

                  {/* Duration (hourly) */}
                  {missionType === "hourly" && (
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.duration_label")} *</label>
                      <Select value={hourlyDuration} onValueChange={setHourlyDuration}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-duration"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">
                          {[{ id: "hourly_3h", label: `3h — ${vehiclePrices[formData.vehicle_type]?.hourly_3h || "—"}€` }, { id: "hourly_6h", label: `6h — ${vehiclePrices[formData.vehicle_type]?.hourly_6h || "—"}€` }, { id: "hourly_10h", label: `10h — ${vehiclePrices[formData.vehicle_type]?.hourly_10h || "—"}€` }].map((d) => (
                            <SelectItem key={d.id} value={d.id} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{d.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Date & Time */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.date_label")} *</label>
                      <Popover><PopoverTrigger asChild>
                        <button type="button" className="input-luxury w-full bg-transparent text-left flex items-center justify-between" data-testid="input-date">
                          <span className={date ? "text-[#1A1A1A]" : "text-[#1A1A1A]/50"}>{date ? format(date, "dd MMMM yyyy", { locale: dateLoc }) : t("deplacements.date_placeholder")}</span>
                          <CalendarIcon size={16} className="text-[#1A1A1A]/50" />
                        </button></PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-[#1A1A1A]/10" align="start"><Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date()} initialFocus className="text-[#1A1A1A]" /></PopoverContent>
                      </Popover>
                    </div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.time_label")} *</label>
                      <Select value={formData.pickup_time} onValueChange={(v) => setFormData((p) => ({ ...p, pickup_time: v }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-time"><Clock size={16} className="text-[#1A1A1A]/50 mr-2" /><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10 max-h-60">{timeSlots.map((tm) => <SelectItem key={tm} value={tm} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{tm}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Addresses */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><MapPin size={14} className="inline mr-1" /> {t("deplacements.pickup_address")} *</label>
                    <AddressAutocomplete value={formData.pickup_address} onChange={(v) => setFormData(p => ({...p, pickup_address: v}))} onPlaceSelect={onPickupPlaceSelect} placeholder={t("deplacements.pickup_placeholder")} required testId="input-pickup" />
                  </div>
                  {missionType === "simple" && (
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><MapPin size={14} className="inline mr-1" /> {t("deplacements.dropoff_address")} *</label>
                      <AddressAutocomplete value={formData.dropoff_address} onChange={(v) => setFormData(p => ({...p, dropoff_address: v}))} onPlaceSelect={onDropoffPlaceSelect} placeholder={t("deplacements.dropoff_placeholder")} required testId="input-dropoff" />
                    </div>
                  )}

                  {/* Detected package badge */}
                  {missionType === "simple" && detectedPkg && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                      className={`p-4 border ${detectedPkg.type === "km" ? "border-[#1A1A1A]/20 bg-[#EDEBE4]" : "border-[#1A1A1A]/30 bg-[#1A1A1A]/5"}`} data-testid="detected-package">
                      <div className="flex items-start gap-3">
                        {DetectedIcon && <DetectedIcon size={22} weight="bold" className="text-[#1A1A1A] mt-0.5 flex-shrink-0" />}
                        <div className="flex-1">
                          <p className="text-[#1A1A1A] text-sm font-medium">{getDetectedLabel()}</p>
                          {detectedPkg.type === "km" ? (
                            <div className="text-[#1A1A1A]/60 text-xs mt-1">
                              {(() => {
                                if (calculatingDistance) return <span>{t("deplacements.calculating_distance")}</span>;
                                if (distanceKm) return <span>{t("deplacements.distance_label")} : <strong className="text-[#1A1A1A]">{distanceKm} km</strong> · {kmRates[formData.vehicle_type]?.rate}€/km · {t("deplacements.min_price")} {detectedPkg.withinParis ? kmRates[formData.vehicle_type]?.minimum_paris : kmRates[formData.vehicle_type]?.minimum_outside}€</span>;
                                return <span>{kmRates[formData.vehicle_type]?.rate}€/km · {t("deplacements.min_price")} {detectedPkg.withinParis ? kmRates[formData.vehicle_type]?.minimum_paris : kmRates[formData.vehicle_type]?.minimum_outside}€</span>;
                              })()}
                            </div>
                          ) : (
                            <p className="text-[#1A1A1A]/60 text-xs mt-1">{t("deplacements.service_label")} : {vehiclePrices[formData.vehicle_type]?.[detectedPkg.id]}€</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Flight/Train number (shown when airport or station detected) */}
                  {missionType === "simple" && isAirportOrStation && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                      <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">
                        {detectedPkg.hasAirport ? <><AirplaneTakeoff size={14} className="inline mr-1" /> {t("deplacements.flight_number")}</> : <><Train size={14} className="inline mr-1" /> {t("deplacements.train_number")}</>}
                      </label>
                      <input type="text" name="flight_train_number" value={formData.flight_train_number} onChange={handleChange}
                        className="input-luxury w-full bg-transparent"
                        placeholder={detectedPkg.hasAirport ? t("deplacements.flight_placeholder") : t("deplacements.train_placeholder")}
                        data-testid="input-flight-train" />
                    </motion.div>
                  )}

                  {/* Passengers & Luggage */}
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><Users size={14} className="inline mr-1" /> {t("deplacements.passengers_label")}</label>
                      <Select value={formData.passengers.toString()} onValueChange={(v) => setFormData((p) => ({ ...p, passengers: parseInt(v) }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-passengers"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">{[1,2,3,4,5,6,7].map((n) => <SelectItem key={n} value={n.toString()} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{n} {n === 1 ? t("deplacements.passenger_singular") : t("deplacements.passenger_plural")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2"><Briefcase size={14} className="inline mr-1" /> {t("deplacements.luggage_label")}</label>
                      <Select value={formData.luggage.toString()} onValueChange={(v) => setFormData((p) => ({ ...p, luggage: parseInt(v) }))}>
                        <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]" data-testid="select-luggage"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-[#1A1A1A]/10">{[0,1,2,3,4,5,6,7,8].map((n) => <SelectItem key={n} value={n.toString()} className="text-[#1A1A1A] hover:bg-[#EDEBE4]">{n} {n <= 1 ? t("deplacements.luggage_singular") : t("deplacements.luggage_plural")}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Observations / Remarks */}
                  <div><label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-2">{t("deplacements.observations_label")}</label>
                    <textarea name="special_requests" value={formData.special_requests} onChange={handleChange} rows={3} className="input-luxury w-full bg-transparent resize-none" placeholder={t("deplacements.observations_placeholder")} data-testid="input-special-requests" /></div>

                  {/* Payment */}
                  <div>
                    <label className="text-[#1A1A1A]/70 text-xs tracking-wide uppercase block mb-4">{t("deplacements.payment_label")} *</label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {[{ val: "online", Icon: CreditCard, label: t("deplacements.payment_online"), desc: t("deplacements.payment_online_desc") }, { val: "onboard", Icon: Money, label: t("deplacements.payment_onboard"), desc: t("deplacements.payment_onboard_desc") }].map(({ val, Icon, label, desc }) => (
                        <label key={val} className={`flex items-center gap-4 p-4 border cursor-pointer transition-all ${formData.payment_method === val ? "border-[#1A1A1A] bg-[#1A1A1A]/5" : "border-[#1A1A1A]/20 hover:border-[#1A1A1A]/40"}`}>
                          <input type="radio" name="payment_method" value={val} checked={formData.payment_method === val} onChange={handleChange} className="accent-[#1A1A1A]" />
                          <div className="flex items-center gap-3"><Icon size={24} weight="light" className="text-[#1A1A1A]" /><div><p className="text-[#1A1A1A] text-sm font-medium">{label}</p><p className="text-[#1A1A1A]/60 text-xs">{desc}</p></div></div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Total & Submit */}
                  <div className="border-t border-[#1A1A1A]/10 pt-6 mt-8">
                    <div className="flex justify-between items-center mb-6">
                      <span className="text-[#1A1A1A]/70">{t("deplacements.total")}</span>
                      <span className="font-heading text-3xl text-[#1A1A1A]" data-testid="total-amount">{total.toFixed(2)}€</span>
                    </div>

                    {draftSaved && (
                      <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-green-50 border border-green-200 p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <Check size={18} weight="bold" className="text-green-600 mt-0.5" />
                          <div>
                            <p className="text-green-800 text-sm font-medium">Brouillon enregistré</p>
                            <p className="text-green-700 text-xs mt-1">Un email avec un lien de modification vous a été envoyé à {formData.client_email}</p>
                            <button type="button" onClick={() => setDraftSaved(null)} className="text-green-600 text-xs underline mt-2">Fermer</button>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="flex gap-3">
                      <button type="submit" disabled={loading || !date || total === 0}
                        className="btn-dark flex-1 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="submit-booking">
                        {loading ? t("deplacements.submitting") : (formData.payment_method === "online" ? t("deplacements.submit_online") : t("deplacements.submit_onboard"))}
                        <ArrowRight size={16} weight="bold" />
                      </button>
                      <button type="button" onClick={handleSaveDraft} disabled={savingDraft || !formData.client_email}
                        className="px-5 py-3 border border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:bg-[#EDEBE4] transition-colors text-xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="save-draft-btn">
                        <FloppyDisk size={16} /> {savingDraft ? "..." : "Brouillon"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DeplacementsPage;
